import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import { listClips, approveClip, type Clip } from '../lib/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function ApprovalPage() {
  const { clipId } = useParams<{ clipId: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [clip, setClip] = useState<Clip | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (user && clipId) {
      loadClip();
    }
  }, [user, clipId]);

  const loadClip = async () => {
    try {
      const data = await listClips(user!.id);
      const found = data.clips.find((c) => c.id === clipId);
      setClip(found || null);
    } catch (error) {
      console.error('Error loading clip:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!clip || !user) return;

    setApproving(true);
    try {
      await approveClip(clip.id, user.id);
      alert('Klipu imeidhinishwa!');
      navigate('/');
    } catch (error) {
      console.error('Error approving clip:', error);
      alert('Hitilafu imetokea');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!clip) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Klipu haijapatikana</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-slate-800">Idhinisha Klipu</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">{clip.title}</h2>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>Duration: {clip.duration.toFixed(1)}s</span>
              <span>Virality Score: {clip.virality_score}</span>
              {clip.sentiment && (
                <span className={`px-2 py-1 rounded-full text-xs ${
                  clip.sentiment === 'positive'
                    ? 'bg-green-100 text-green-700'
                    : clip.sentiment === 'negative'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-slate-100 text-slate-700'
                }`}>
                  {clip.sentiment}
                </span>
              )}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-slate-700 mb-2">Transcript</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-slate-800 leading-relaxed">{clip.transcript}</p>
            </div>
          </div>

          {clip.caption && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Caption</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-900">{clip.caption}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            {clip.pa_approved === 1 ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={20} />
                <span className="font-medium">Imeidhinishwa</span>
              </div>
            ) : (
              <>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {approving ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <CheckCircle size={20} />
                  )}
                  Sawa, Post
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                >
                  <XCircle size={20} />
                  Rudisha
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
